import { recordAnalyticsEvent } from '../api/analytics';
import type { RecordEventPayload } from '../types/systemTypes';

const CONSENT_KEY = 'imagineEngineCookieConsent';
const SESSION_KEY = 'imagineEngineAnalyticsSession';
let consentStatus: string = 'essential_only';
let initialized: boolean = false;
let defaultContext: Record<string, unknown> = {};
let cachedSessionId: string | null = null;

function readStoredConsent(): string | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.analytics ? 'analytics' : 'essential_only';
  } catch (error) {
    console.warn('analyticsClient: unable to read consent from storage', error);
    return null;
  }
}

function getSessionId(): string {
  if (cachedSessionId) return cachedSessionId;
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      cachedSessionId = stored;
      return cachedSessionId;
    }
    const generated = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, generated);
    cachedSessionId = generated;
    return cachedSessionId;
  } catch (error) {
    console.warn('analyticsClient: unable to persist session id', error);
    return `sess-${Date.now()}`;
  }
}

export function initializeAnalyticsClient(): void {
  if (initialized) return;
  const stored = readStoredConsent();
  if (stored) {
    consentStatus = stored;
  }
  initialized = true;
}

export function setAnalyticsConsent(allowAnalytics: boolean): void {
  consentStatus = allowAnalytics ? 'analytics' : 'essential_only';
}

export function getAnalyticsConsent(): string {
  if (!initialized) {
    initializeAnalyticsClient();
  }
  return consentStatus;
}

export function setDefaultAnalyticsContext(context: Record<string, unknown> = {}): void {
  defaultContext = { ...context };
}

function coerceUserId(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

export async function trackEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
  context: Record<string, unknown> = {}
): Promise<void> {
  if (!initialized) {
    initializeAnalyticsClient();
  }
  if (consentStatus !== 'analytics') {
    return;
  }

  const sessionId = getSessionId();
  const payloadContext: Record<string, unknown> = {
    ...defaultContext,
    ...context,
    sessionId
  };

  const userId =
    coerceUserId(payloadContext.userId) ??
    coerceUserId(context.userId) ??
    coerceUserId(properties.userId);

  try {
    const payload: RecordEventPayload = {
      eventName,
      properties,
      context: payloadContext,
      consentStatus,
      analyticsVersion: 'v1',
      sessionId,
      userId
    };
    await recordAnalyticsEvent(payload);
  } catch (error) {
    console.warn('analyticsClient: failed to record event', error);
  }
}

export function trackPageView(pathname: string, metadata: Record<string, unknown> = {}): void {
  trackEvent('page_view', metadata, { pathname });
}

export async function backfillSessionUserId(): Promise<void> {
  const sessionId = getSessionId();
  if (!sessionId) return;
  
  try {
    const { backfillUserIdForSession } = await import('../api/analytics');
    await backfillUserIdForSession(sessionId);
  } catch (error) {
    // Silently fail - this is a best-effort operation
    console.warn('analyticsClient: failed to backfill userId for session', error);
  }
}

